import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  FormControlLabel,
  RadioGroup,
  Alert,
} from '@mui/material';
import { Play, Pause, Square, Clock, AlertTriangle } from 'lucide-react';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';

import { useAppSelector, useAppDispatch } from '@/store';
import {
  selectActiveTimers,
  selectShowStopTimerDialog,
  selectPendingTimerTask,
  pauseTimerAsync,
  resumeTimerAsync,
  stopTimerAsync,
  startTimerAsync,
  closeStopTimerDialog,
  MAX_CONCURRENT_TIMERS,
} from '@/store/slices/timerSlice';
import { TimerStatus, ActiveTimer } from '@/types';
import { formatTimerSeconds, getTimerDisplaySeconds } from '@/shared/utils/timerDisplay';

/**
 * SingleTimerCard - Renders a single timer display
 */
interface SingleTimerCardProps {
  timer: ActiveTimer;
  isRunning: boolean;
  index: number;
  totalTimers: number;
  onPauseResume: (taskId: string) => void;
  onStop: (taskId: string, taskTitle?: string) => void;
}

const SingleTimerCard: React.FC<SingleTimerCardProps> = ({
  timer,
  isRunning,
  index,
  totalTimers,
  onPauseResume,
  onStop,
}) => {
  const timerIsRunning = timer.status === TimerStatus.RUNNING;

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: timerIsRunning ? 'success.50' : 'warning.50',
        borderRadius: 2,
        border: '1px solid',
        borderColor: timerIsRunning ? 'success.200' : 'warning.200',
        mb: totalTimers > 1 && index === 0 ? 2 : 0,
      }}
    >
      {/* Header with timer badge and task name */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Clock
            size={16}
            className={timerIsRunning ? 'timer-running' : ''}
            style={{ color: timerIsRunning ? '#16a34a' : '#d97706' }}
          />
          <Chip
            label={`#${index + 1}`}
            size="small"
            color={timerIsRunning ? 'success' : 'warning'}
            sx={{ height: 20, fontSize: '0.65rem', minWidth: 32 }}
          />
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {timer.taskTitle || 'Task'}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: timerIsRunning ? 'success.700' : 'warning.700',
            ml: 1,
          }}
        >
          {timerIsRunning ? 'Running' : 'Paused'}
        </Typography>
      </Box>

      {/* Timer Display */}
      <Typography
        variant="h5"
        sx={{
          fontFamily: 'monospace',
          fontWeight: 700,
          color: timerIsRunning ? 'success.700' : 'warning.700',
          letterSpacing: '1px',
          textAlign: 'center',
          mb: 1.5,
        }}
      >
        {formatTimerSeconds(getTimerDisplaySeconds(timer), true)}
      </Typography>

      {/* Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
        <Tooltip title={timerIsRunning ? 'Pause' : 'Resume'}>
          <IconButton
            size="small"
            onClick={() => onPauseResume(timer.taskId)}
            sx={{
              width: 36,
              height: 36,
              bgcolor: timerIsRunning ? 'warning.100' : 'success.100',
              '&:hover': {
                bgcolor: timerIsRunning ? 'warning.200' : 'success.200',
              },
            }}
          >
            {timerIsRunning ? (
              <Pause size={18} style={{ color: '#d97706' }} />
            ) : (
              <Play size={18} style={{ color: '#16a34a' }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Stop & Save">
          <IconButton
            size="small"
            onClick={() => onStop(timer.taskId, timer.taskTitle)}
            sx={{
              width: 36,
              height: 36,
              bgcolor: 'error.100',
              '&:hover': { bgcolor: 'error.200' },
            }}
          >
            <Square size={16} style={{ color: '#dc2626' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

/**
 * TimerOptionItem - Radio option for selecting timer to stop
 */
interface TimerOptionItemProps {
  timer: ActiveTimer;
  index: number;
  selected: boolean;
}

const TimerOptionItem: React.FC<TimerOptionItemProps> = ({ timer, index, selected }) => {
  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 1,
        bgcolor: selected ? 'primary.50' : 'background.paper',
        cursor: 'pointer',
      }}
    >
      <FormControlLabel
        value={timer.taskId}
        control={<Radio size="small" />}
        label={
          <Box sx={{ ml: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Chip
                label={`#${index + 1}`}
                size="small"
                color={timer.status === TimerStatus.RUNNING ? 'success' : 'warning'}
                sx={{ height: 18, fontSize: '0.65rem', minWidth: 28 }}
              />
              <Typography variant="body2" fontWeight={600}>
                {timer.taskTitle || 'Task'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Time logged:
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              >
                {formatTimerSeconds(getTimerDisplaySeconds(timer), true)}
              </Typography>
              <Chip
                label={timer.status === TimerStatus.RUNNING ? 'Running' : 'Paused'}
                size="small"
                color={timer.status === TimerStatus.RUNNING ? 'success' : 'warning'}
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            </Box>
          </Box>
        }
        sx={{ m: 0, width: '100%' }}
      />
    </Box>
  );
};

/**
 * StopTimerDialog - Dialog to select which timer to stop when max is reached
 */
interface StopTimerDialogProps {
  open: boolean;
  timers: ActiveTimer[];
  pendingTaskTitle?: string;
  onClose: () => void;
  onStopAndStart: (taskIdToStop: string) => void;
}

const StopTimerDialog: React.FC<StopTimerDialogProps> = ({
  open,
  timers,
  pendingTaskTitle,
  onClose,
  onStopAndStart,
}) => {
  const [selectedTimer, setSelectedTimer] = React.useState<string>(timers[0]?.taskId || '');

  React.useEffect(() => {
    if (timers.length > 0 && !selectedTimer) {
      setSelectedTimer(timers[0].taskId);
    }
  }, [timers, selectedTimer]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AlertTriangle size={24} style={{ color: '#f59e0b' }} />
        Maximum Timers Reached
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          You can only run <strong>{MAX_CONCURRENT_TIMERS} timers</strong> simultaneously. 
          Please stop one of the running timers to start a new one.
        </Alert>

        {pendingTaskTitle && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="primary" fontWeight={600}>
              Task to start:
            </Typography>
            <Typography variant="body2">{pendingTaskTitle}</Typography>
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Select a timer to stop:
        </Typography>

        <RadioGroup
          value={selectedTimer}
          onChange={(e) => setSelectedTimer(e.target.value)}
        >
          {timers.map((timer, index) => (
            <TimerOptionItem 
              key={timer.taskId} 
              timer={timer} 
              index={index}
              selected={selectedTimer === timer.taskId}
            />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => onStopAndStart(selectedTimer)}
          disabled={!selectedTimer}
        >
          Stop Selected & Start New
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * ActiveTimerWidget
 * Prominent timer display on dashboard - Now supports multiple timers (max 2)
 * Primary action widget for time tracking
 */
const ActiveTimerWidget: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeTimers = useAppSelector(selectActiveTimers);
  const showStopDialog = useAppSelector(selectShowStopTimerDialog);
  const pendingTask = useAppSelector(selectPendingTimerTask);
  const [stopConfirm, setStopConfirm] = useState<{ taskId: string; taskTitle?: string } | null>(null);
  const anyRunning = activeTimers.some((t) => t.status === TimerStatus.RUNNING);

  const handlePauseResume = (taskId: string) => {
    const timer = activeTimers.find(t => t.taskId === taskId);
    if (timer?.status === TimerStatus.RUNNING) {
      dispatch(pauseTimerAsync(taskId));
    } else {
      dispatch(resumeTimerAsync(taskId));
    }
  };

  const handleStop = (taskId: string, _taskTitle?: string) => {
    setStopConfirm({ taskId, taskTitle: _taskTitle });
  };

  const handleCloseDialog = () => {
    dispatch(closeStopTimerDialog());
  };

  const handleStopAndStart = async (taskIdToStop: string) => {
    if (!pendingTask) {
      return;
    }

    try {
      await dispatch(stopTimerAsync({ taskId: taskIdToStop })).unwrap();
      dispatch(closeStopTimerDialog());
      await dispatch(
        startTimerAsync({
          taskId: pendingTask.taskId,
          userId: pendingTask.userId,
          taskTitle: pendingTask.taskTitle,
        })
      ).unwrap();
    } catch {
      // Errors surface via timerStartError snackbar in Layout.
    }
  };

  // No active timer state
  if (activeTimers.length === 0) {
    return (
      <Card
        sx={{
          bgcolor: 'background.default',
          border: '2px dashed',
          borderColor: 'divider',
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Clock size={32} style={{ color: '#94a3b8', marginBottom: 12 }} />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No Active Timers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start a timer from your task list
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            (Max {MAX_CONCURRENT_TIMERS} timers can run simultaneously)
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Clock size={20} style={{ color: anyRunning ? '#16a34a' : '#d97706' }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Active Timers
              </Typography>
            </Box>
            <Tooltip title={`You can run up to ${MAX_CONCURRENT_TIMERS} timers at the same time`}>
              <Chip
                label={`${activeTimers.length} running`}
                size="small"
                color={activeTimers.length >= MAX_CONCURRENT_TIMERS ? 'warning' : 'primary'}
                variant="outlined"
              />
            </Tooltip>
          </Box>

          {/* Timer Cards */}
          {activeTimers.map((timer, index) => (
            <SingleTimerCard
              key={timer.taskId}
              timer={timer}
              isRunning={timer.status === TimerStatus.RUNNING}
              index={index}
              totalTimers={activeTimers.length}
              onPauseResume={handlePauseResume}
              onStop={handleStop}
            />
          ))}

          {/* Info message when at max capacity */}
          {activeTimers.length >= MAX_CONCURRENT_TIMERS && (
            <Alert severity="info" sx={{ mt: 2 }} icon={<AlertTriangle size={18} />}>
              <Typography variant="caption">
                Maximum timers running. Stop one to start another.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Stop Timer Dialog */}
      <StopTimerDialog
        open={showStopDialog}
        timers={activeTimers}
        pendingTaskTitle={pendingTask?.taskTitle}
        onClose={handleCloseDialog}
        onStopAndStart={handleStopAndStart}
      />

      <ConfirmationModal
        open={!!stopConfirm}
        onClose={() => setStopConfirm(null)}
        data={
          stopConfirm
            ? {
                title: 'Stop timer',
                message: 'Stop the timer and log the time.',
                confirmText: 'Stop',
                type: 'warning',
                reasonInput: {
                  label: 'Reason',
                  placeholder: 'Why are you stopping this timer?',
                  required: true,
                },
                onConfirm: (reason) => {
                  if (stopConfirm) dispatch(stopTimerAsync({ taskId: stopConfirm.taskId, reason }));
                  setStopConfirm(null);
                },
              }
            : undefined
        }
      />
    </>
  );
};

export default ActiveTimerWidget;
